import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import Layout from "../components/Layout";
import CallsTable, { SortField } from "../components/grants/calls/CallsTable";
import CallsColumnsPanel from "../components/grants/calls/CallsColumnsPanel";
import { useCallsColumnConfig } from "../components/grants/calls/useCallsColumnConfig";
import { FilterBar } from "../components/grants/calls/filters/FilterBar";
import { FilterModal } from "../components/grants/calls/filters/FilterModal";
import { filterRegistry } from "../components/grants/calls/filters/registry";
import { useFilterState } from "../components/grants/calls/filters/useFilterState";
import { StatsBar } from "../components/grants/stats";
import CreateCallModal from "../components/grants/calls/CreateCallModal";
import CreateAlertModal from "../components/grants/alerts/CreateAlertModal";
import { CallLabelManagementModal, useCallLabels } from "../components/grants/labels";
import SavedViewsBar from "../components/grants/saved-views/SavedViewsBar";
import OnboardingTour from "../components/onboarding/OnboardingTour";
import { useOnboardingTour } from "../hooks/useOnboardingTour";
import { getGrantsCallsTourSteps, TOUR_PAGES } from "../config/onboardingTourConfig";
import apiClient from "../services/api";
import { useDebounce } from "../hooks/useDebounce";
import { useTurnstileContext } from "../hooks/useTurnstile";
import { CallItem, Pagination, StatusCount, ProgramCount, CompetitionCount, AlertFilters, AlertListResponse, TargetGroupCount, DistrictCount, LabelCount } from "../types/grants";
import { SavedViewItem, SavedViewFilters, SchemeValue } from "../services/grantsApi";
import { useHotel } from "../contexts/HotelContext";
import { EmptyState } from "../components/common/EmptyState";

import { TurnstileErrorMessage } from "../components/grants/TurnstileErrorMessage";
import { getDocsUrl } from "../utils/domainConfig";
import { formatNaboryCount } from "../utils/text.utils";
import { useUniqueRegions } from "../hooks/useUniqueRegions";
import { useBarometerBatch } from "../hooks/useBarometerBatch";
import { isValidUUID } from "../utils/url-validation";

/**
 * Allowed sortBy values for URL param validation.
 * Must match ALLOWED_SORT_FIELDS in backend find-all-calls.dto.ts
 * and SortField type in CallsTable.tsx.
 */
const ALLOWED_SORT_FIELDS: SortField[] = ["endDate", "startDate", "createdAt", "title"];

interface CallsResponse {
  calls: CallItem[];
  pagination: Pagination;
  statusCounts: StatusCount[];
  programCounts: ProgramCount[];
  competitionCounts: CompetitionCount[];
  targetGroupCounts: TargetGroupCount[];
  districtCounts: DistrictCount[];
  labelCounts?: LabelCount[];
  allCalls?: CallItem[];
}

/**
 * Page component for displaying active grant calls in a table format.
 * Uses plugin-based filter architecture (FilterBar + FilterModal + useFilterState).
 */
const EMPTY_CALL_IDS: string[] = [];

const grantsCallsTourSteps = getGrantsCallsTourSteps();

const GrantsCalls: React.FC = () => {
  const queryClient = useQueryClient();
  const { hasModuleEnabled, user, isLoading: isHotelLoading } = useHotel();
  // M4 (review): Świadoma akceptacja — niezalogowani użytkownicy współdzielą
  // klucz `calls-column-config-v1-anonymous` w localStorage w danej przeglądarce.
  // Brak PII (tylko konfiguracja widoku kolumn). Po zalogowaniu hook czyta klucz
  // per-user. Patrz: docs/specifications/2026-05-27-konfiguracja-widoku-naborow/.
  const userId = user?.id ?? "anonymous";
  const isAuthenticated = !!user?.id;
  const { isCookieReady } = useTurnstileContext();
  const canWrite = hasModuleEnabled("admin:kiedynabory:write");
  const hasLabelsModule = hasModuleEnabled("grants:schedule");
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingCallId, setEditingCallId] = useState<string | null>(null);
  const [isLabelModalOpen, setIsLabelModalOpen] = useState(false);

  // Column configuration (per-user localStorage). Hoisted from CallsTable
  // so the "Kolumny" trigger lives next to FilterBar (spec: §Trigger "Kolumny").
  const {
    visibleColumns,
    allColumns,
    toggleColumn,
    reorderColumns,
    resetToDefault,
  } = useCallsColumnConfig(userId);
  const [isColumnsPanelOpen, setIsColumnsPanelOpen] = useState(false);
  const columnsTriggerRef = useRef<HTMLButtonElement>(null);

  // State for "Save as alert" modal
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [alertInitialFilters, setAlertInitialFilters] = useState<AlertFilters | undefined>(undefined);
  const alertTriggerRef = useRef<HTMLButtonElement>(null);

  // Plugin-based filter state (reads/writes URL params for all 13 filters)
  const {
    filterValues,
    toApiParams,
    toSavedViewFilters,
    applySavedView,
    advancedFiltersCount,
    hasActiveFilters,
    toAlertFilters,
  } = useFilterState(filterRegistry);

  // ---- Page, Limit, View: non-filter URL params managed separately ----

  // Active saved view ID from URL (?view=<uuid>)
  const activeViewId = useMemo(() => {
    const viewParam = searchParams.get("view");
    return viewParam && isValidUUID(viewParam) ? viewParam : null;
  }, [searchParams]);

  // Read page/limit from URL
  const page = useMemo(() => {
    const p = searchParams.get("page");
    return p ? parseInt(p, 10) : 1;
  }, [searchParams]);

  const limit = useMemo(() => {
    const l = searchParams.get("limit");
    return l ? parseInt(l, 10) : 50;
  }, [searchParams]);

  /**
   * Helper to read and validate current sorting params from URL.
   * Sorting is managed by CallsTable via setSearchParams; these helpers
   * ensure other URL-rebuilding functions preserve them.
   */
  const getValidatedSortParams = useCallback((): { sortBy?: string; sortOrder?: string } => {
    const existingSortBy = searchParams.get("sortBy");
    const existingSortOrder = searchParams.get("sortOrder");
    if (existingSortBy && ALLOWED_SORT_FIELDS.includes(existingSortBy as SortField)) {
      const result: { sortBy: string; sortOrder?: string } = { sortBy: existingSortBy };
      if (existingSortOrder === "ASC" || existingSortOrder === "DESC") {
        result.sortOrder = existingSortOrder;
      }
      return result;
    }
    return {};
  }, [searchParams]);

  /**
   * Append validated sort params to a URLSearchParams object.
   * Used by all URL-rebuilding functions to preserve sorting state.
   */
  const appendSortParams = useCallback(
    (params: URLSearchParams) => {
      const sort = getValidatedSortParams();
      if (sort.sortBy) {
        params.set("sortBy", sort.sortBy);
        if (sort.sortOrder) {
          params.set("sortOrder", sort.sortOrder);
        }
      }
    },
    [getValidatedSortParams]
  );

  /**
   * Rebuild URL params from filter state + page/limit/view.
   * Used as a helper for operations that need to coordinate filter changes
   * with page/limit/view params (which are outside the filter plugin system).
   */
  const buildFullUrl = useCallback(
    (options?: { page?: number; limit?: number; viewId?: string | null; clearSort?: boolean }) => {
      // Start with filter params from plugin system
      const params = new URLSearchParams();

      // Serialize active filter values
      for (const plugin of filterRegistry) {
        const value = filterValues[plugin.key] ?? plugin.defaultValue;
        if (plugin.isActive(value)) {
          plugin.toUrlParams(value, params);
        }
      }

      // Add non-filter params
      const newPage = options?.page ?? page;
      const newLimit = options?.limit ?? limit;
      if (newPage > 1) params.set("page", newPage.toString());
      if (newLimit !== 50) params.set("limit", newLimit.toString());

      // Preserve sorting params (managed by CallsTable via setSearchParams)
      if (!options?.clearSort) {
        appendSortParams(params);
      }

      // View param: null means clear, undefined means preserve, string means set
      const viewId = options?.viewId;
      if (viewId === null) {
        // Explicitly clear view
      } else if (viewId !== undefined) {
        params.set("view", viewId);
      } else {
        // Preserve existing view
        const existingView = searchParams.get("view");
        if (existingView && isValidUUID(existingView)) {
          params.set("view", existingView);
        }
      }

      return params;
    },
    [filterValues, page, limit, searchParams, appendSortParams]
  );

  /**
   * Wrapper for setFilterValue that preserves page/limit/view params.
   * Resets page to 1 on any filter change (same behavior as old code).
   */
  const handleFilterChange = useCallback(
    (key: string, value: unknown) => {
      // Build new filter values with the change
      const newValues = { ...filterValues, [key]: value };

      // Rebuild URL with new filter values + page reset + preserved limit/view
      const params = new URLSearchParams();
      for (const plugin of filterRegistry) {
        const v = newValues[plugin.key] ?? plugin.defaultValue;
        if (plugin.isActive(v)) {
          plugin.toUrlParams(v, params);
        }
      }

      // Reset page to 1 on filter change, preserve limit
      if (limit !== 50) params.set("limit", limit.toString());

      // Preserve sorting params
      appendSortParams(params);

      // Preserve view param
      const existingView = searchParams.get("view");
      if (existingView && isValidUUID(existingView)) {
        params.set("view", existingView);
      }

      setSearchParams(params, { replace: true });
    },
    [filterValues, limit, searchParams, setSearchParams, appendSortParams]
  );

  /**
   * Clear all filters: resets filter state + page to 1, clears view param,
   * clears sorting, preserves limit.
   */
  const handleClearFilters = useCallback(() => {
    const params = new URLSearchParams();
    if (limit !== 50) params.set("limit", limit.toString());
    // Clear view param and sorting (do not preserve)
    setSearchParams(params, { replace: true });
  }, [limit, setSearchParams]);

  /**
   * Handle advanced modal apply: merge draft values into URL,
   * reset page to 1, preserve limit, view, and sorting.
   */
  const handleAdvancedFiltersApply = useCallback(
    (draftValues: Record<string, unknown>) => {
      // Merge main filter values with advanced draft values
      const mergedValues = { ...filterValues, ...draftValues };

      const params = new URLSearchParams();
      for (const plugin of filterRegistry) {
        const v = mergedValues[plugin.key] ?? plugin.defaultValue;
        if (plugin.isActive(v)) {
          plugin.toUrlParams(v, params);
        }
      }

      // Reset page to 1, preserve limit
      if (limit !== 50) params.set("limit", limit.toString());

      // Preserve sorting params
      appendSortParams(params);

      // Preserve view param
      const existingView = searchParams.get("view");
      if (existingView && isValidUUID(existingView)) {
        params.set("view", existingView);
      }

      setSearchParams(params, { replace: true });
    },
    [filterValues, limit, searchParams, setSearchParams, appendSortParams]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      const params = buildFullUrl({ page: newPage });
      setSearchParams(params, { replace: true });
    },
    [buildFullUrl, setSearchParams]
  );

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      const params = buildFullUrl({ page: 1, limit: newLimit });
      setSearchParams(params, { replace: true });
    },
    [buildFullUrl, setSearchParams]
  );

  // Debounce filter values for API calls (prevents excessive requests)
  const debouncedFilterValues = useDebounce(filterValues, 300);

  const sortParams = getValidatedSortParams();
  const { data, isLoading, isFetching, error, refetch } = useQuery<CallsResponse>({
    queryKey: ["grants-calls", debouncedFilterValues, page, limit, sortParams.sortBy, sortParams.sortOrder],
    // Gate: don't fetch until Turnstile cookie is set (prevents 403 on first render)
    enabled: isCookieReady,
    // Keep previous data visible during refetches to prevent filter dropdowns
    // from unmounting (skeleton flash) and losing their internal isOpen state.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      // Build API params from plugin system
      const params = toApiParams();

      // Add pagination params
      if (page) params.append("page", page.toString());
      if (limit) params.append("limit", limit.toString());

      // Add sorting params (managed by CallsTable, read from URL)
      const sort = getValidatedSortParams();
      if (sort.sortBy) {
        params.append("sortBy", sort.sortBy);
        if (sort.sortOrder) {
          params.append("sortOrder", sort.sortOrder);
        }
      }

      const { data } = await apiClient.get(`/grants/calls?${params}`);
      return data;
    },
  });

  // Fetch alert limit info for "Save as alert" button
  const { data: alertData } = useQuery<AlertListResponse>({
    queryKey: ["grants-alerts"],
    queryFn: async () => {
      const { data } = await apiClient.get("/grants/alerts");
      return data;
    },
    staleTime: 30_000,
  });

  const alertLimitReached = (alertData?.remaining ?? 1) <= 0;

  // Use call labels hook for label management (only when module is enabled)
  const callIds = useMemo(
    () => (data?.calls || []).map((c) => c.id),
    [data?.calls]
  );
  const {
    labels: callLabels,
    articleLabels,
    createLabel: handleCreateLabel,
    createAndAssign: handleCreateAndAssign,
    updateLabel: handleUpdateLabel,
    deleteLabel: handleDeleteLabel,
    setCallLabel: handleSetCallLabel,
  } = useCallLabels({ callIds: hasLabelsModule ? callIds : EMPTY_CALL_IDS });

  // Extract unique regions from all calls, normalized to ASCII slugs
  const uniqueRegions = useUniqueRegions(data?.calls || [], data?.allCalls);

  // Batch-fetch barometer data for all visible calls with LABOUR_BAROMETER capability
  const { getCallBarometer } = useBarometerBatch(data?.calls || []);

  // Handle clicking a saved view pill: apply its filters + set ?view=<id>
  const handleViewSelect = useCallback((view: SavedViewItem) => {
    const viewFilters = view.filters as SavedViewFilters;

    // Use applySavedView to set filter values from saved view
    applySavedView(viewFilters);

    // After applySavedView sets the URL, we need to also set view param.
    // Since applySavedView calls setSearchParams internally, we do a follow-up
    // to add the view param. Instead, rebuild the full URL ourselves.
    const params = new URLSearchParams();
    for (const plugin of filterRegistry) {
      const value = plugin.fromSavedView(viewFilters);
      if (plugin.isActive(value)) {
        plugin.toUrlParams(value, params);
      }
    }
    if (limit !== 50) params.set("limit", limit.toString());
    params.set("view", view.id);
    setSearchParams(params, { replace: true });
  }, [limit, applySavedView, setSearchParams]);

  // Handle clicking "Wszystkie": clear all filters and ?view=
  const handleClearView = useCallback(() => {
    const params = new URLSearchParams();
    if (limit !== 50) params.set("limit", limit.toString());
    // No view param = cleared
    setSearchParams(params, { replace: true });
  }, [limit, setSearchParams]);

  // Handle clicking KFS/BUR preset pill or clearing scheme.
  // Per spec AC-5 + DD-1:
  // - AC-5: choosing a preset clears ONLY ?view= (not other filters)
  // - DD-1: scheme combines with other filters via AND (e.g. preset BUR +
  //   programId = `scheme=bur&programId=<x>`)
  //
  // Therefore we build new params FROM current `searchParams` (preserving
  // status, region, programId, etc.), delete ?view=, and set/delete ?scheme=
  // atomically in a single navigation. This avoids the need for a separate
  // onClearView() call in SavedViewsBar.handlePresetClick (no race condition,
  // no double navigation).
  const handleSchemeChange = useCallback(
    // SavedViewsBar's onSchemeChange is shared with the articles context, whose
    // scheme union includes "pozostale". Calls never emits it (calls presets are
    // KFS/BUR only); accept the wider type and ignore the articles-only value.
    (scheme: SchemeValue | "pozostale" | undefined) => {
      const params = new URLSearchParams(searchParams);
      // AC-5: clear ?view= when a preset is chosen.
      params.delete("view");
      if (scheme !== undefined && scheme !== "pozostale") {
        params.set("scheme", scheme);
      } else {
        params.delete("scheme");
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Build program map (UUID -> name) for auto-suggest view name
  const programMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    data?.programCounts?.forEach((p) => {
      if (p.id && p.name) map[p.id] = p.name;
    });
    return map;
  }, [data?.programCounts]);

  // Current filters as SavedViewFilters (for SavedViewsBar)
  const currentSavedViewFilters = useMemo<SavedViewFilters>(
    () => toSavedViewFilters(),
    [toSavedViewFilters]
  );

  const handleEditCall = (call: CallItem) => {
    setEditingCallId(call.id);
    setIsCreateModalOpen(true);
  };

  // Handle "Save as alert" button click
  const handleSaveAsAlert = useCallback(
    (
      _sanitizedFilters: AlertFilters,
      triggerRef: React.RefObject<HTMLButtonElement | null>
    ) => {
      // Store the trigger ref for focus management
      (alertTriggerRef as React.MutableRefObject<HTMLButtonElement | null>).current = triggerRef.current;
      // Generate sanitized filters from current filter state
      const sanitized = toAlertFilters();
      setAlertInitialFilters({ ...sanitized });
      setIsAlertModalOpen(true);
    },
    [toAlertFilters]
  );

  // Handle alert modal close
  const handleAlertModalClose = () => {
    setIsAlertModalOpen(false);
    setAlertInitialFilters(undefined);
  };

  // Handle alert creation success
  const handleAlertSuccess = () => {
    setIsAlertModalOpen(false);
    setAlertInitialFilters(undefined);
    queryClient.invalidateQueries({ queryKey: ["grants-alerts"] });
  };

  // Treat cookie-not-ready as loading (query is disabled, so isLoading stays false)
  const effectivelyLoading = isLoading || !isCookieReady;

  // Onboarding tour - waits for data to load (not during skeleton)
  const { shouldRunTour, markTourCompleted } = useOnboardingTour({
    pageKey: TOUR_PAGES["grants-calls"].pageKey,
    version: TOUR_PAGES["grants-calls"].version,
    userId: user?.id ?? null,
    isLoading: isHotelLoading || effectivelyLoading || !data,
  });

  // Show a helpful message after 30s of loading with no data
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  useEffect(() => {
    if (!effectivelyLoading || data) {
      setLoadingTimedOut(false);
      return;
    }
    const timer = setTimeout(() => setLoadingTimedOut(true), 30_000);
    return () => clearTimeout(timer);
  }, [effectivelyLoading, data]);

  // Get status filter values for hideStatusColumn logic
  const statusFilterValue = (filterValues.status as string[]) ?? [];

  return (
    <Layout>
      <OnboardingTour
        shouldRunTour={shouldRunTour}
        steps={grantsCallsTourSteps}
        onComplete={markTourCompleted}
      />
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">
              Nabory - Aktywne
            </h1>
            <p className="mt-1 text-sm text-neutral-600">
              Lista aktywnych naborów na dofinansowania
            </p>
          </div>
          {canWrite && (
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
            >
              Dodaj nabór
            </button>
          )}
        </div>

        {/* Stats bar - dismissable, above filters */}
        <StatsBar variant="calls" dismissable className="mb-4" />

        <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
          {/* Saved views bar - integrated as card header */}
          <SavedViewsBar
            currentUrlParams={searchParams}
            activeViewId={activeViewId}
            onViewSelect={handleViewSelect}
            onClearView={handleClearView}
            onViewNotFound={handleClearView}
            onSchemeChange={handleSchemeChange}
            currentFilters={currentSavedViewFilters}
            programMap={programMap}
          />

          <div className="p-2">
            {/* Plugin-based filter bar + "Kolumny" trigger (spec: pozycja po prawej obok "Więcej") */}
            <div className="flex items-start gap-2 relative">
              <div className="flex-1 min-w-0">
                <FilterBar
                  registry={filterRegistry}
                  filterValues={filterValues}
                  onFilterChange={handleFilterChange}
                  onClearAll={handleClearFilters}
                  onAdvancedFiltersClick={() => setIsAdvancedFiltersOpen(true)}
                  advancedFiltersCount={advancedFiltersCount}
                  hasActiveFilters={hasActiveFilters}
                  isLoading={effectivelyLoading && !data}
                  isFetching={isFetching && !!data}
                  totalCount={data?.pagination?.total ?? data?.calls?.length}
                  facets={{
                    programCounts: data?.programCounts,
                    competitionCounts: data?.competitionCounts,
                    statusCounts: data?.statusCounts,
                    regions: uniqueRegions,
                    districtCounts: data?.districtCounts,
                    labelCounts: hasLabelsModule ? data?.labelCounts : undefined,
                  }}
                  onSaveAsAlert={handleSaveAsAlert}
                  alertLimitReached={alertLimitReached}
                />
              </div>
              {/* Spec: trigger "Kolumny" tylko dla zalogowanych użytkowników (per-user localStorage). */}
              {isAuthenticated && (
                <div className="relative pt-0">
                  <button
                    ref={columnsTriggerRef}
                    onClick={() => setIsColumnsPanelOpen((prev) => !prev)}
                    aria-expanded={isColumnsPanelOpen}
                    aria-haspopup="dialog"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                    Kolumny
                  </button>
                  {isColumnsPanelOpen && (
                    <CallsColumnsPanel
                      allColumns={allColumns}
                      visibleColumns={visibleColumns}
                      onToggle={toggleColumn}
                      onReorder={reorderColumns}
                      onReset={resetToDefault}
                      forcedVisibleIds={hasLabelsModule ? ["label"] : []}
                      onClose={() => {
                        setIsColumnsPanelOpen(false);
                        columnsTriggerRef.current?.focus();
                      }}
                      triggerRef={columnsTriggerRef}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Plugin-based advanced filters modal */}
            <FilterModal
              isOpen={isAdvancedFiltersOpen}
              registry={filterRegistry}
              filterValues={filterValues}
              facets={{
                targetGroupCounts: data?.targetGroupCounts,
              }}
              onApply={handleAdvancedFiltersApply}
              onClose={() => setIsAdvancedFiltersOpen(false)}
            />

            {/* Loading state - only for initial load (includes cookie verification) */}
            {effectivelyLoading && !data ? (
              <div className="py-12 text-center" role="status" aria-live="polite">
                {loadingTimedOut ? (
                  <>
                    <svg
                      className="h-12 w-12 mx-auto mb-3 text-amber-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    <p className="font-medium text-neutral-900">
                      Nie udało się załadować naborów
                    </p>
                    <p className="mt-1 text-sm text-neutral-600 max-w-md mx-auto">
                      Możliwe przyczyny: brak połączenia z serwerem, rozszerzenia
                      blokujące (np.&nbsp;AdBlock, Brave&nbsp;Shields) lub problem
                      z weryfikacją przeglądarki.
                    </p>
                    <div className="mt-4 flex items-center justify-center gap-3">
                      <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        Odśwież stronę
                      </button>
                      {getDocsUrl("/troubleshooting/weryfikacja-przegladarki") && (
                        <a
                          href={getDocsUrl("/troubleshooting/weryfikacja-przegladarki")}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          Dowiedz się więcej
                        </a>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="mt-2 text-sm text-neutral-600">
                      Ładowanie naborów...
                    </p>
                  </>
                )}
              </div>
            ) : error ? (
              <div className="py-12 text-center" role="alert">
                <div className="text-red-600 mb-4">
                  <svg
                    className="h-12 w-12 mx-auto mb-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <p className="font-medium">Błąd podczas pobierania naborów</p>
                  <TurnstileErrorMessage error={error} />
                </div>
                <button
                  onClick={() => refetch()}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Spróbuj ponownie
                </button>
              </div>
            ) : (
              <>
                {/* Results summary with fetching indicator */}
                <div className="mt-4 mb-4 flex items-center justify-between">
                  <p className="text-sm text-neutral-600" aria-live="polite">
                    {formatNaboryCount(data?.pagination?.total ?? data?.calls?.length ?? 0)}
                    {isFetching && (
                      <span className="ml-2 inline-flex items-center">
                        <span className="animate-spin h-3 w-3 border-2 border-primary-600 border-t-transparent rounded-full mr-1"></span>
                        <span className="text-neutral-400">aktualizacja...</span>
                      </span>
                    )}
                  </p>
                <select
                  value={limit || 50}
                  onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                  className="px-3 py-1 text-sm border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:outline-none"
                >
                  <option value={25}>25 na stronę</option>
                  <option value={50}>50 na stronę</option>
                  <option value={100}>100 na stronę</option>
                </select>
              </div>

              {/* Empty state */}
              {data?.calls?.length === 0 ? (
                <EmptyState
                  icon={
                    <svg
                      className="mx-auto h-12 w-12"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  }
                  title="Brak naborów do wyświetlenia"
                  description={
                    hasActiveFilters
                      ? "Nie znaleziono naborów dla wybranych filtrów. Spróbuj zmienić kryteria wyszukiwania."
                      : "Aktualnie brak aktywnych naborów. Nowe nabory pojawią się automatycznie po ich wykryciu przez system."
                  }
                  action={
                    hasActiveFilters
                      ? { label: "Wyczyść filtry", onClick: handleClearFilters }
                      : undefined
                  }
                />
              ) : (
                <>
                  <CallsTable
                    calls={data?.calls || []}
                    isAdmin={canWrite}
                    onEdit={handleEditCall}
                    hideStatusColumn={statusFilterValue.length === 1}
                    userId={userId}
                    visibleColumns={visibleColumns}
                    allColumns={allColumns}
                    labels={hasLabelsModule ? callLabels : undefined}
                    articleLabels={hasLabelsModule ? articleLabels : undefined}
                    onSetCallLabel={hasLabelsModule ? async (callId, labelId) => {
                      const result = await handleSetCallLabel(callId, labelId);
                      refetch();
                      return result;
                    } : undefined}
                    onCreateAndAssign={hasLabelsModule ? async (name, callId) => {
                      const result = await handleCreateAndAssign(name, callId);
                      refetch();
                      return result;
                    } : undefined}
                    onManageLabels={hasLabelsModule ? () => setIsLabelModalOpen(true) : undefined}
                    getCallBarometer={getCallBarometer}
                  />

                  {/* Pagination */}
                  {data?.pagination && data.pagination.totalPages > 1 && (
                    <nav
                      className="mt-6 flex items-center justify-between"
                      role="navigation"
                      aria-label="Paginacja naborów"
                    >
                      <button
                        onClick={() =>
                          handlePageChange(data.pagination.currentPage - 1)
                        }
                        disabled={!data.pagination.hasPrevious}
                        aria-label="Przejdź do poprzedniej strony"
                        className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-primary-500 focus:outline-none"
                      >
                        Poprzednia
                      </button>
                      <span
                        className="text-sm text-neutral-600"
                        aria-current="page"
                      >
                        Strona {data.pagination.currentPage} z{" "}
                        {data.pagination.totalPages}
                      </span>
                      <button
                        onClick={() =>
                          handlePageChange(data.pagination.currentPage + 1)
                        }
                        disabled={!data.pagination.hasNext}
                        aria-label="Przejdź do następnej strony"
                        className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-primary-500 focus:outline-none"
                      >
                        Następna
                      </button>
                    </nav>
                  )}
                </>
              )}
              </>
            )}
          </div>
        </div>

        <CreateCallModal
          isOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false);
            setEditingCallId(null);
          }}
          callId={editingCallId}
        />

        {/* Save as Alert Modal */}
        <CreateAlertModal
          isOpen={isAlertModalOpen}
          onClose={handleAlertModalClose}
          onSuccess={handleAlertSuccess}
          editingAlert={null}
          initialFilters={alertInitialFilters}
          triggerRef={alertTriggerRef}
        />

        {/* Label Management Modal */}
        {hasLabelsModule && (
          <CallLabelManagementModal
            isOpen={isLabelModalOpen}
            onClose={() => setIsLabelModalOpen(false)}
            labels={callLabels}
            labelCounts={data?.labelCounts}
            onUpdateLabel={async (id, payload) => {
              await handleUpdateLabel(id, payload);
              refetch();
            }}
            onDeleteLabel={async (id) => {
              await handleDeleteLabel(id);
              refetch();
            }}
            onCreateLabel={async (name, color) => {
              await handleCreateLabel(name, color);
              refetch();
            }}
          />
        )}
      </div>
    </Layout>
  );
};

export default GrantsCalls;
